import {Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {Subscription} from 'rxjs';
import {InstagramService} from 'src/app/services/social/instagram.service';

@Component({
  selector: 'app-instagram-post',
  templateUrl: './instagram-post.component.html',
})
export class InstagramPostComponent implements OnInit, OnDestroy {
  @Output() onRefresh = new EventEmitter<void>();
  media: any = {};
  comments: any = [];
  pendingDeleteId;
  deletingId;
  igCommentsLoading = false;
  currentSlide = 0;
  postLiked = false;
  showEmojiPicker = false;
  newCommentText = '';
  replyingToComment: any = null;
  accountDetails: any = {};
  emojiList = [
    '😀', '😂', '😍', '😎', '😭', '😤', '😊', '🥰',
    '😅', '😆', '🤣', '😇', '🥳', '😏', '😒', '😓',
    '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍',
    '🔥', '✨', '💯', '🎉', '👏', '🙌', '👍', '👎',
    '😢', '😩', '🤔', '🤩', '😋', '😜', '🤪', '😝',
    '🙏', '💪', '🫶', '👀', '💀', '🫠', '😤', '🥲',
    '🚀', '🌟', '💫', '⚡', '🎯', '💥', '🌈', '🎊',
  ];
  @ViewChild('videoElement') videoElement: ElementRef;
  @ViewChild('commentInputRef') commentInputRef: ElementRef;
  @ViewChild('commentsScroll') commentsScroll: ElementRef;
  private postSubscription: Subscription;

  constructor(private instagramService: InstagramService) {
    this.loadAccountDetails();
  }

  private loadAccountDetails() {
    this.instagramService.instagramDetails().subscribe(
      response => {
        this.accountDetails = (response && response.data && response.data.payload) || {};
      },
      err => {
        console.error('Failed to load Instagram account details', err);
        this.accountDetails = {};
      }
    );
  }

  ngOnInit() {
    this.postSubscription = this.instagramService.postToView.subscribe((payload: any) => {
      if (!payload || !Object.keys(payload).length) { return; }

      // Support both legacy shape (plain post object) and new shape ({ post, commentId, replyId, parentCommentId })
      const post = payload.post || payload;
      const targetCommentId: string | null = payload.post ? (payload.commentId || null) : null;
      const targetReplyId: string | null = payload.post ? (payload.replyId || null) : null;
      const targetParentCommentId: string | null = payload.post ? (payload.parentCommentId || null) : null;
      if (!Object.keys(post).length) { return; }

      if (post.item === "mentions") {

        this.media = {...post, ...post.instagramObject};
        this.openPostModal();
        return;
      }
      this.igCommentsLoading = true;
      this.instagramService.fetchComments(post.id).subscribe((comments: any) => {
        this.igCommentsLoading = false;
        this.comments = (comments.data.payload || []).map((c: any) => ({
            ...c,
            liked: false,
            showReplies: false,
            replies: ((c.replies && c.replies.data) || []).map((r: any) => ({...r, liked: false})),
          }));

          this.media = payload.post;

          const modalEl = document.getElementById("instagramPostModal");
          const wasAlreadyOpen = modalEl && modalEl.classList.contains('show');
          this.openPostModal();
          if (wasAlreadyOpen) {
            this._signalModalReady();
          } else {
            let ready = false;
            const onReady = () => {
              if (ready) return;
              ready = true;
              if (modalEl) modalEl.removeEventListener('shown.bs.modal', onReady);
              this._signalModalReady();
            };
            if (modalEl) {
              modalEl.addEventListener('shown.bs.modal', onReady);
              if (!modalEl.classList.contains('show')) {
                modalEl.addEventListener('hidden.bs.modal', () => {
                  if (!ready) this.openPostModal();
                }, { once: true });
              }
            }
            setTimeout(onReady, 2000);
          }
          this._refreshVideo();

          setTimeout(() => this._handleDeepLink(payload.action, targetCommentId, targetReplyId, targetParentCommentId), 400);
      });
    });
  }

  _handleDeepLink(action: string, commentId: string | null, replyId: string | null, parentCommentId: string | null) {
    // Determine which comment to auto-reply to
    let targetComment: any = null;

    if (replyId && parentCommentId) {
      // Find parent, expand replies, scroll to reply row, reply to the reply
      targetComment = this.comments.find((c: any) => c.id == parentCommentId);
      if (targetComment) {
        targetComment.showReplies = true;
        const targetReply = targetComment.replies.find((r: any) => r.id === replyId);
        if (targetReply) {
          setTimeout(() => {
            this._scrollToItem(replyId);
            if (action === 'reply') {
              this.replyTo(targetReply);           // sets up reply-to banner + focuses input
            }
          }, 150);
        }
      }
      return;
    }

    if (commentId) {
      targetComment = this.comments.find((c: any) => c.id === commentId);
      if (targetComment) {
        setTimeout(() => {
          this._scrollToItem(commentId);
          if (action === 'reply') {
            this.replyTo(targetComment);           // sets up reply-to banner + focuses input
          }
        }, 150);
      }
    }
  }

  _scrollToItem(id: string) {
    const container = this.commentsScroll.nativeElement;
    if (!container) { return; }

    const el = container.querySelector(`[data-comment-id="${id}"]`);
    if (!el) { return; }

    el.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // Brief highlight pulse
    el.classList.add('ig-comment-highlight');
    setTimeout(() => el.classList.remove('ig-comment-highlight'), 1500);
  }
  private _signalModalReady() {
    this.instagramService.postModalReady$.next();
  }

  private _refreshVideo() {
    setTimeout(() => {
      if (this.media && this.media.media_type === 'VIDEO' && this.videoElement && this.videoElement.nativeElement) {
        this.videoElement.nativeElement.load();
      }
    });
  }

  openPostModal() {
    if (!this.accountDetails || !this.accountDetails.username) {
      this.loadAccountDetails();
    }
    const modalEl = document.getElementById("instagramPostModal");
    if (!modalEl) return;
    if (modalEl.classList.contains('show')) return;
    const btn = document.getElementById("instagramPostModalBtn");
    if (btn) btn.click();
  }

  ngOnDestroy() {
    if (this.postSubscription) {
      this.postSubscription.unsubscribe();
    }
  }

  onCloseModal() {
    if (this.videoElement && this.videoElement.nativeElement) {
      this.videoElement.nativeElement.pause();
      this.videoElement.nativeElement.removeAttribute('src');
      this.videoElement.nativeElement.load();
      this.videoElement.nativeElement.currentTime = 0;
    }
    this.media = {};
    this.comments = [];
    this.instagramService.postToView.next({});
  }

  prevSlide() {
    if (this.currentSlide > 0) {
      this.currentSlide = this.currentSlide - 1;
    }
  }

  nextSlide() {
    if (this.currentSlide < (this.media.children.data.length - 1)) {
      this.currentSlide = this.currentSlide + 1;
    }
  }

  gotoSlide(i: number) {
    this.currentSlide = i;
  }

  get activeMedia() {
    if (this.media.media_type === 'CAROUSEL_ALBUM' && this.media.children.data.length) {
      return this.media.children.data[this.currentSlide];
    }
    return this.media;
  }


  replyTo(comment: any) {
    this.replyingToComment = comment;
    this.newCommentText = `@${comment.username} `;
    // focus input
    setTimeout(() => this.commentInputRef.nativeElement.focus(), 100);
  }

  timeAgo(timestamp: string): string {
    const now = new Date();
    const date = new Date(timestamp);
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (seconds < 60) {
      return 'Just now';
    }
    if (minutes === 1) {
      return '1 minute ago';
    }
    if (minutes < 60) {
      return `${minutes} minutes ago`;
    }
    if (hours === 1) {
      return '1 hour ago';
    }
    if (hours < 24) {
      return `${hours} hours ago`;
    }
    if (days === 1) {
      return 'Yesterday';
    }
    if (days === 2) {
      return '2 days ago';
    }

    // after 2 days return formatted date
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  submitComment() {
    console.log('submitting comment...');
    const text = this.newCommentText.trim();
    if (!text) {
      return;
    }

    if (this.replyingToComment) {
      this.instagramService.replyComment(this.replyingToComment.id, text)
        .subscribe({
          next: (res: any) => {
            // optimistic update
            console.log({res});
            if (!this.replyingToComment.replies) {
              this.replyingToComment.replies = [];
            }
            this.replyingToComment.replies.push({
              id: res.data.id || Date.now(),
              username: this.accountDetails.username,
              text,
              timestamp: new Date().toISOString(),
              liked: false,
            });
            this.replyingToComment.showReplies = true;
            this.replyingToComment = null;
            this.newCommentText = '';
            this.onRefresh.emit();
            this.scrollToBottom();
          },
          error: (err) => {
            console.error('reply failed', err);
          }
        });
    } else {
      this.instagramService.postComment(text, this.media.id).subscribe((comment: any) => {
        this.comments = [{
          id: comment.data.payload.id, username: this.accountDetails.username || 'You', text,
          timestamp: this.getIgFormattedDate(), like_count: 0, liked: false,
          showReplies: false, replies: []
        }, ...this.comments
        ].sort((a, b) => a.timestamp - b.timestamp);

      });

    }

    this.newCommentText = '';
    setTimeout(() => {
      const el = this.commentsScroll.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    }, 50);
  }

  cancelReply() {
    this.replyingToComment = null;
    this.newCommentText = '';
  }

  getIgFormattedDate(date = new Date()) {
    return date.toISOString().replace(/\.\d{3}Z$/, '+0000');
  }

  scrollToBottom() {
    setTimeout(() => {
      const el = this.commentsScroll.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    }, 50);
  }

  deleteComment(comment: any) {
    console.log('deleted comment...');
    this.deletingId = comment.id;
    this.instagramService.deleteComment(comment.id).subscribe({
      next: () => {

        this.comments = this.comments.filter((c: any) => c.id !== comment.id);
        this.deletingId = null;
        this.pendingDeleteId = null;
        this.onRefresh.emit();
      },
      error: (err) => {
        console.error('deleted comment failed', err);
        this.deletingId = null;
        this.pendingDeleteId = null;
      }
    });
  }

  deleteReply(parent: any, reply: any) {
    this.deletingId = reply.id;
    this.instagramService.deleteComment(reply.id).subscribe({
      next: () => {
        parent.replies = parent.replies.filter((r: any) => r.id !== reply.id);
        this.deletingId = null;
        this.pendingDeleteId = null;
        this.onRefresh.emit();
      },
      error: (err) => {
        this.deletingId = null;
        this.pendingDeleteId = null;
        console.error('delete reply failed', err);
      }
    });
  }
  confirmDelete(id: string): void {
    this.pendingDeleteId = id;
  }
  cancelDelete(): void {
    this.pendingDeleteId = null;
  }
  canDelete(comment: any): boolean {
    if (!comment || !comment.username || !this.accountDetails || !this.accountDetails.username) {
      return false;
    }
    return comment.username === this.accountDetails.username;
  }

  getInitials(username: string): string {
    if (!username) {
      return 'IG';
    }
    return username.slice(0, 2).toUpperCase();
  }

  toggleEmojiPicker(event: Event) {
    event.stopPropagation();
    this.showEmojiPicker = !this.showEmojiPicker;
  }

  selectEmoji(emoji: string) {
    this.newCommentText = (this.newCommentText || '') + emoji;
    this.showEmojiPicker = false;
    setTimeout(() => this.commentInputRef.nativeElement.focus(), 50);
  }

  formatDiv(text: string){
    if (!text) return '';
    return text.replace(/\n/g, '<br>');
  }
}

